import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  DollarSign, TrendingDown, Calculator, Receipt, Upload, Download,
  Plus, Trash2, Percent, PlayCircle, ArrowLeft, Clock
} from 'lucide-react';
import { shortSku } from '../utils/sku';

const EXPENSE_CATEGORIES = ['Nomina', 'Arriendo', 'Servicios', 'Empaque', 'Transporte', 'Publicidad', 'Software', 'Impuestos', 'Otros'];
const EXPENSE_TYPES = ['Fijo (mensual)', 'Variable (por orden)'];

function ExpensesTab() {
  const [expenses, setExpenses] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [summary, setSummary] = useState({ fixed: 0, variable: 0, total: 0 });
  const [form, setForm] = useState({ concept: '', amount: '', category: 'Otros', type: 'Fijo (mensual)' });
  const [taxForm, setTaxForm] = useState({ concept: '', percentage: '' });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('last_30_days');
  const [importReport, setImportReport] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, taxRes, sumRes] = await Promise.all([
        api.get('/expenses'),
        api.get('/tax-entries'),
        api.get(`/expenses/summary?period=${period}`),
      ]);
      setExpenses(expRes.data.expenses || []);
      setTaxes(taxRes.data.taxes || []);
      setSummary(sumRes.data || { fixed: 0, variable: 0, total: 0 });
    } catch (err) {
      console.error('Error loading expenses:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddExpense = async () => {
    if (!form.concept || !form.amount) return toast.error('Completa concepto y monto');
    try {
      await api.post('/expenses', {
        concept: form.concept,
        amount: parseFloat(form.amount),
        category: form.category,
        type: form.type === 'Fijo (mensual)' ? 'fixed' : 'variable',
      });
      setForm({ concept: '', amount: '', category: 'Otros', type: 'Fijo (mensual)' });
      toast.success('Gasto registrado');
      loadData();
    } catch { toast.error('Error al registrar gasto'); }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Gasto eliminado');
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddTax = async () => {
    if (!taxForm.concept || !taxForm.percentage) return toast.error('Completa concepto y porcentaje');
    try {
      await api.post('/tax-entries', {
        concept: taxForm.concept,
        percentage: parseFloat(taxForm.percentage),
      });
      setTaxForm({ concept: '', percentage: '' });
      toast.success('Impuesto registrado');
      loadData();
    } catch { toast.error('Error al registrar impuesto'); }
  };

  const handleDeleteTax = async (id) => {
    try {
      await api.delete(`/tax-entries/${id}`);
      toast.success('Impuesto eliminado');
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/expenses/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_gastos.csv';
      link.click();
    } catch { toast.error('Error al descargar plantilla'); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const result = await api.post('/expenses/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportReport(result.data);
      toast.success(`${result.data.imported} gastos importados; ${result.data.rejected || 0} rechazados`);
      loadData();
    } catch { toast.error('Error al importar CSV'); }
  };

  const formatCurrency = (val) => '$' + (val || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 });

  return (
      <div className="space-y-5">
      <div className="flex items-center justify-between"><h2 className="text-sm text-slate-400">Periodo aplicado</h2><select value={period} onChange={e => setPeriod(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"><option value="last_7_days">Últimos 7 días</option><option value="last_15_days">Últimos 15 días</option><option value="last_30_days">Últimos 30 días</option><option value="last_60_days">Últimos 60 días</option></select></div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Gastos Fijos (mes)', value: summary.fixed, icon: TrendingDown, color: 'text-red-400' },
          { label: 'Gastos Variables', value: summary.variable, icon: DollarSign, color: 'text-amber-400' },
          { label: 'Total Costos', value: summary.total, icon: Receipt, color: 'text-red-400' },
        ].map(item => (
          <div key={item.label} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <item.icon size={16} className="text-slate-400" />
              <span className="text-xs text-slate-400">{item.label}</span>
            </div>
            <p className={`text-2xl font-bold ${item.color}`}>{formatCurrency(item.value)}</p>
          </div>
        ))}
      </div>

      {/* Register Expense */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Registrar Gasto</h3>
        <div className="flex flex-wrap items-end gap-3">
          <input
            data-testid="expense-concept"
            type="text"
            placeholder="Concepto"
            value={form.concept}
            onChange={e => setForm({ ...form, concept: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 flex-1 min-w-[150px]"
          />
          <input
            data-testid="expense-amount"
            type="number"
            placeholder="Monto"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 w-32"
          />
          <select
            data-testid="expense-category"
            value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
          >
            {EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select
            data-testid="expense-type"
            value={form.type}
            onChange={e => setForm({ ...form, type: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
          >
            {EXPENSE_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <button
            data-testid="expense-add-btn"
            onClick={handleAddExpense}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white rounded-lg text-sm font-medium transition-all"
          >
            <Plus size={16} />
            Agregar
          </button>
        </div>
      </div>

      {/* Expense list */}
      {expenses.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800/50">
                <th className="text-left py-3 px-4 text-xs text-slate-400 font-medium">Concepto</th>
                <th className="text-left py-3 px-4 text-xs text-slate-400 font-medium">Categoría</th>
                <th className="text-left py-3 px-4 text-xs text-slate-400 font-medium">Tipo</th>
                <th className="text-right py-3 px-4 text-xs text-slate-400 font-medium">Monto</th>
                <th className="text-right py-3 px-4 text-xs text-slate-400 font-medium"></th>
              </tr>
            </thead>
            <tbody>
          {expenses.map(exp => (
                <tr key={exp.id || exp._id} className="border-b border-slate-800/30 hover:bg-slate-800/30">
                  <td className="py-3 px-4 text-white">{exp.concept}</td>
                  <td className="py-3 px-4 text-slate-400">{exp.category}</td>
                  <td className="py-3 px-4 text-slate-400">{exp.type === 'fixed' ? 'Fijo' : 'Variable'}</td>
                  <td className="py-3 px-4 text-right text-red-400 font-medium">{formatCurrency(exp.amount)}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => handleDeleteExpense(exp.id || exp._id)} className="text-slate-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk Upload */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-lg font-bold text-white mb-3">Carga Masiva</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            data-testid="download-template-btn"
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 hover:bg-slate-700 text-white rounded-lg text-sm transition-colors"
          >
            <Download size={16} />
            Descargar Plantilla
          </button>
          <input
            data-testid="csv-upload-input"
            type="file"
            accept=".csv"
            onChange={handleUploadCSV}
            className="text-sm text-slate-400"
          />
          <button
            data-testid="csv-upload-btn"
            onClick={() => document.querySelector('[data-testid="csv-upload-input"]')?.click()}
            className="flex items-center gap-2 px-4 py-2 border border-slate-700 hover:bg-slate-800 text-white rounded-lg text-sm transition-colors"
          >
            <Upload size={16} />
            Subir
          </button>
        </div>
      </div>
      {importReport && <div data-testid="import-report" className="text-sm text-slate-300 bg-slate-900/50 border border-slate-800 rounded-lg p-4">Importados: {importReport.imported} · Rechazados: {importReport.rejected} · Vacíos: {importReport.skipped}{importReport.errors?.length > 0 && <ul className="mt-2 text-red-400">{importReport.errors.map((e, i) => <li key={i}>Fila {e.row}: {e.message}</li>)}</ul>}</div>}

      {/* Tax Entries */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Percent size={18} className="text-blue-400" />
          <h3 className="text-lg font-bold text-white">Tasas para la estimación fiscal</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          IVA se calcula como impuesto incluido en ventas y se acredita contra COGS, comisiones y envío configurados. ISR sobre ventas usa ventas sin IVA. El Dashboard muestra una estimación; no sustituye el cálculo del contador.
        </p>
        <button data-testid="initialize-tax-defaults-btn" onClick={async () => { try { await api.post('/tax-entries/initialize-defaults'); loadData(); toast.success('Impuestos inicializados'); } catch { toast.error('No se pudieron inicializar'); } }} className="mb-4 px-3 py-2 border border-blue-500/30 text-blue-400 rounded-lg text-sm">Inicializar IVA 16% / ISR 2.5%</button>
        <div className="flex items-end gap-3 mb-4">
          <input
            data-testid="tax-concept"
            type="text"
            placeholder="Concepto (ej: IVA, Renta)"
            value={taxForm.concept}
            onChange={e => setTaxForm({ ...taxForm, concept: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 flex-1"
          />
          <div className="relative">
            <input
              data-testid="tax-percentage"
              type="number"
              placeholder="Porcentaje (%)"
              value={taxForm.percentage}
              onChange={e => setTaxForm({ ...taxForm, percentage: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 pr-8 text-sm text-white placeholder-slate-500 w-40"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
          </div>
          <button
            data-testid="tax-add-btn"
            onClick={handleAddTax}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            Agregar Impuesto
          </button>
        </div>
        {taxes.length > 0 && (
          <div className="space-y-2">
            {taxes.map(tax => (
              <div key={tax.id || tax._id} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-2.5">
                <span className="text-sm text-white">{tax.concept}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-blue-400 font-medium">{tax.percentage}%</span>
                  <button onClick={() => handleDeleteTax(tax.id || tax._id)} className="text-slate-500 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function COGSTab() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, withCogs: 0 });
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/products/lite');
      const prods = res.data.products || [];
      setProducts(prods);
      const withCogs = prods.filter(p => p.cogs && p.cogs > 0).length;
      setStats({ total: prods.length, withCogs });
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleUpdateCogs = async (productId, cogs) => {
    try {
      await api.put(`/profitability/cogs/${productId}`, { cogs: parseFloat(cogs) });
      toast.success('COGS actualizado');
    } catch { toast.error('Error al actualizar COGS'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/products/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_cogs.csv';
      link.click();
    } catch { toast.error('Error al descargar plantilla'); }
  };

  const handleUploadCOGS = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/products/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('COGS importados');
      loadProducts();
    } catch { toast.error('Error al importar COGS'); }
  };

  const progressPct = stats.total > 0 ? Math.round((stats.withCogs / stats.total) * 100) : 0;

  const filtered = products.filter(p => {
    const matchSearch = !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase());
    if (filter === 'with_cogs') return matchSearch && p.cogs > 0;
    if (filter === 'without_cogs') return matchSearch && (!p.cogs || p.cogs === 0);
    return matchSearch;
  });

  return (
    <div className="space-y-5">
      {/* Progress + Total */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-amber-400" />
              <div>
                <h3 className="text-lg font-bold text-white">Progreso de Costos</h3>
                <p className="text-xs text-slate-500">Productos con COGS asignado</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-amber-400">{progressPct}%</p>
          </div>
          <div className="w-full bg-slate-800 rounded-full h-2 mt-3">
            <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>{stats.withCogs} con costo</span>
            <span className="text-red-400">{stats.total - stats.withCogs} sin costo</span>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-1">
            <Receipt size={16} className="text-slate-400" />
            <span className="text-xs text-slate-400">Total Productos</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.total.toLocaleString()}</p>
        </div>
      </div>

      {/* Bulk upload COGS */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Upload size={18} className="text-blue-400" />
          <h3 className="text-lg font-bold text-white">Carga Masiva de Costos</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Descarga la plantilla con tus productos, completa la columna COGS y súbela.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={handleDownloadTemplate} className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 hover:bg-slate-700 text-white rounded-lg text-sm transition-colors">
            <Download size={16} />
            Descargar Plantilla
          </button>
          <input type="file" accept=".csv" onChange={handleUploadCOGS} className="text-sm text-slate-400" />
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Upload size={16} />
            Subir COGS
          </button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[250px] relative">
          <input
            data-testid="cogs-search"
            type="text"
            placeholder="Buscar por titulo, SKU o ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 pl-10 text-sm text-white placeholder-slate-500"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">🔍</span>
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white"
        >
          <option value="all">Todos ({stats.total})</option>
          <option value="with_cogs">Con COGS ({stats.withCogs})</option>
          <option value="without_cogs">Sin COGS ({stats.total - stats.withCogs})</option>
        </select>
        <button onClick={loadProducts} className="flex items-center gap-2 px-4 py-2.5 border border-slate-700 hover:bg-slate-800 text-white rounded-lg text-sm transition-colors">
          ↻ Actualizar
        </button>
      </div>

      {/* Products table */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800/50">
              <th className="text-left py-3 px-4 text-xs text-slate-400 font-medium">Producto ↑</th>
              <th className="text-left py-3 px-4 text-xs text-slate-400 font-medium">SKU</th>
              <th className="text-center py-3 px-4 text-xs text-slate-400 font-medium">COGS (Costo)</th>
              <th className="text-center py-3 px-4 text-xs text-slate-400 font-medium">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="py-8 text-center text-slate-500">Cargando productos...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={4} className="py-8 text-center text-slate-500">No hay productos</td></tr>
            ) : filtered.slice(0, 50).map(prod => (
              <tr key={prod.id || prod.meli_id} className="border-b border-slate-800/30 hover:bg-slate-800/30">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    {prod.thumbnail && <img src={prod.thumbnail} alt="" className="w-10 h-10 rounded object-cover bg-slate-800" />}
                    <div>
                      <p className="text-white text-sm truncate max-w-[250px]">{prod.title}</p>
                      <p className="text-xs text-slate-500">{prod.meli_id}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-slate-400 font-mono text-xs">{shortSku(prod.sku) || '-'}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-slate-500">$</span>
                    <input
                      type="number"
                      defaultValue={prod.cogs || 0}
                      onBlur={e => handleUpdateCogs(prod.id || prod.meli_id, e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white w-20 text-center"
                    />
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className={`w-3 h-3 rounded-full mx-auto ${prod.cogs > 0 ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CalculatorTab() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Calculator size={48} className="text-slate-600 mb-4" />
      <h3 className="text-xl font-bold text-slate-400 mb-2">Próximamente</h3>
      <p className="text-sm text-slate-500 max-w-md">
        La calculadora de rentabilidad estará disponible próximamente. Podrás calcular márgenes, precios óptimos y más.
      </p>
    </div>
  );
}

export default function CostosPage() {
  const [activeTab, setActiveTab] = useState('expenses');

  const tabs = [
    { id: 'expenses', label: 'Gastos Operativos', icon: DollarSign, testId: 'tab-expenses' },
    { id: 'cogs', label: 'Costos de Producto (COGS)', icon: Receipt, testId: 'tab-cogs' },
    { id: 'calculator', label: 'Calculadora', icon: Calculator, testId: 'tab-calculator', badge: 'Próximamente' },
  ];

  return (
    <DashboardLayout
      title="Gestión de Costos"
      subtitle="Gastos operativos y costos de producto (COGS)"
      icon={Receipt}
      backTo="/dashboard"
      headerRight={
        <button className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors">
          <PlayCircle size={16} />
          Ver tutorial
        </button>
      }
    >
      {/* Tabs */}
      <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-1.5 flex items-center gap-1 mb-6 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={tab.testId}
            onClick={() => tab.id !== 'calculator' && setActiveTab(tab.id)}
            className={`relative px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-blue-600/20 text-blue-400'
                : tab.id === 'calculator'
                ? 'text-slate-500 cursor-default'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
            title={tab.badge || ''}
          >
            <tab.icon size={15} />
            {tab.label}
            {tab.badge && (
              <span className="text-[10px] text-slate-500 ml-1">({tab.badge})</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'expenses' && <ExpensesTab />}
      {activeTab === 'cogs' && <COGSTab />}
      {activeTab === 'calculator' && <CalculatorTab />}
    </DashboardLayout>
  );
}
